<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { ref, defineEmits } from "vue";
import AppSelect from "@/components/inputs/AppSelect.vue";
import CheckBox from "@/components/inputs/CheckBox.vue";
import CogIcon from "@/components/icons/CogIcon.vue";

const calendarYears = Array.from({ length: 200 }, (_, i) => 1901 + i);
const calendarMonths = [
  "tiger",
  "rabbit",
  "dragon",
  "snake",
  "horse",
  "goat",
  "monkey",
  "rooster",
  "dog",
  "pig",
  "rat",
  "ox",
];

const { t } = useI18n();

const emits = defineEmits<{
  (e: "update:yearStars", year: number, month: number): void;
  (e: "update:monthStars", year: number, month: number): void;
  (e: "update:buildYear", buildYear: number): void;
  (
    e: "toggle:showStars",
    showStars: boolean,
    buildYear: number,
    yearStars: number,
    monthStars: number
  ): void;
}>();

const showStars = ref<boolean>(false);
const showStarsControls = ref<boolean>(false);
const buildYear = ref<number>(new Date().getFullYear());
const yearStars = ref<number>(new Date().getFullYear());
const monthStars = ref<number>(new Date().getMonth());

function handleChangeYear(year: number): void {
  yearStars.value = year;
  emits("update:yearStars", year, monthStars.value + 1);
}

function handleChangeMonth(month: string): void {
  monthStars.value = calendarMonths.indexOf(month);
  emits("update:monthStars", yearStars.value, monthStars.value + 1);
}

function handleBuildYear(year: number) {
  buildYear.value = year;
  emits("update:buildYear", Number(year));
}

function toggleShowSettings() {
  showStarsControls.value = !showStarsControls.value;
}

function toggleShowStars() {
  showStars.value = !showStars.value;
  emits(
    "toggle:showStars",
    showStars.value,
    buildYear.value,
    yearStars.value,
    monthStars.value
  );
}
</script>

<template>
  <div
    :class="[
      'flex-col m-[10px] p-3 rounded-xl bg-white flex absolute top-0 left-0 z-[1001] overflow-hidden',
      'transition-all duration-300 ease-in-out',
      showStarsControls ? 'w-[278px]' : 'w-12 sm:w-[200px]',
    ]"
  >
    <button
      class="w-full flex items-center justify-between"
      @click="toggleShowSettings"
    >
      <CogIcon class="sm:hidden size-6" />
      <span class="hidden sm:block text-xl">
        {{ t("calculators.azm.flying-stars") }}
      </span>
      <svg
        :class="[
          'size-8 transform transition-transform hidden sm:block',
          showStarsControls ? 'rotate-180' : 'rotate-0',
        ]"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>
    <div
      class="grid grid-cols-[max-content_max-content] items-center justify-between gap-y-2 pt-2"
      v-if="showStarsControls"
    >
      <p class="text-sm text-left">{{ t("calculators.azm.year-stars") }}</p>
      <AppSelect
        class="w-[140px]"
        :items="calendarYears"
        :default-value="yearStars"
        @select-option="(year: number) => handleChangeYear(Number(year))"
      />

      <p class="text-sm text-left">{{ t("calculators.azm.month-stars") }}</p>
      <AppSelect
        class="w-[140px]"
        :items="calendarMonths"
        :default-value="calendarMonths[monthStars]"
        @select-option="(month: string) => handleChangeMonth(month)"
      >
        <template #option="{ item }">
          {{ t(`calculators.azm.months.${item}`) }}
        </template>
      </AppSelect>

      <p class="text-sm text-left">{{ t("labels.build-year") }}</p>
      <AppSelect
        class="w-[140px]"
        :items="calendarYears"
        :default-value="buildYear"
        @select-option="handleBuildYear"
      />
      <CheckBox
        class="col-span-2"
        input-id="move-image"
        :label="t('calculators.azm.show-flying-stars')"
        :isEnabled="showStars"
        @toggle="toggleShowStars"
      />
    </div>
  </div>
</template>
